/**
 * The chat transport seam.
 *
 * These interfaces describe what the chat needs from a message transport, in
 * our own terms. Twilio Conversations is one implementation, adapted in
 * {@link ./twilioTransport}; the fake, polling and read-only conversations are
 * others. Nothing outside an adapter may depend on a provider's SDK types, so
 * a transport backed by our own messages API can replace Twilio without the
 * chat changing.
 *
 * Add a member only when a consumer reads it, and only in a shape every
 * transport can provide. `attributes` is `unknown` because the parse layer
 * validates it anyway.
 */

/** A message in a conversation. */
export interface ChatMessage {
  /** Stable across updates. Twilio's message SID, or our own message id. */
  readonly id: string;
  /** Position in the conversation, consecutive from zero. */
  readonly index: number;
  /** The author's user id as a string, or null when the transport has none. */
  readonly author: string | null;
  readonly body: string | null;
  readonly attributes: unknown;
  readonly createdAt: Date | null;
  readonly updatedAt: Date | null;
}

export interface ChatConversationLastMessage {
  index?: number;
  createdAt?: Date;
}

export type ChatConversationStatus = 'active' | 'inactive' | 'closed';

/** A page of messages, oldest first. */
export interface ChatMessagePage {
  items: ChatMessage[];
  hasPrevPage: boolean;
}

/** A file already uploaded for a message being sent. */
export interface OutgoingChatFile {
  readonly s3Key: string;
  readonly fileName: string;
  readonly noPHI: true;
}

/** A message the viewer is sending. */
export interface OutgoingChatMessage {
  /**
   * Generated when Send is pressed and reused for every retry of this
   * message. It comes back on the stored message's attributes as
   * `clientMessageId`, which is how a pending message is matched to it.
   */
  readonly clientMessageId: string;
  readonly body: string;
  readonly files?: readonly OutgoingChatFile[];
  /** The message's typed data, such as an expert relay or Primum Support attribution. */
  readonly data?: { readonly type: string };
  /** The reply draft the message was composed from. */
  readonly replyDraftId?: string;
}

/** What a conversation guarantees about sending. */
export interface ChatSendCapabilities {
  /**
   * True when sending the same `clientMessageId` again yields the message
   * already stored rather than a second one. Only then may the chat show a
   * message before the server confirms it and retry it on its own: without
   * it, a retry after a send that reached the server posts twice.
   */
  readonly idempotentSend: boolean;
}

export type ChatMessageListener = (message: ChatMessage) => void;

/** Stops a listener. Safe to call more than once. */
export type Unsubscribe = () => void;

/** One conversation's messages, read state and live events. */
export interface ChatConversation {
  readonly id: string;
  readonly attributes: unknown;
  /** Index of the last message the viewer has read, or null for none. */
  readonly lastReadIndex: number | null;
  readonly lastMessage?: ChatConversationLastMessage;
  readonly status?: ChatConversationStatus;
  /**
   * Up to `pageSize` messages, oldest first. Without an anchor, the newest
   * page; with one, the newest page ending at that index, inclusive. Inclusive
   * because that is Twilio's rule and the callers were written against it:
   * `getMessages(1, 0)` reads the first message, and the chat pages back by
   * anchoring on the oldest index it holds and dropping the repeat by id.
   */
  getMessages(pageSize?: number, anchor?: number): Promise<ChatMessagePage>;
  /** Moves the viewer's read horizon to the newest message. */
  markAllRead(): Promise<void>;
  readonly capabilities: ChatSendCapabilities;
  /**
   * Sends a message as the viewer. Resolves with the stored message when the
   * send itself returns it, or undefined when it arrives later through
   * `onMessageAdded`; either way it carries the `clientMessageId`. Rejects
   * with the server's error, which the chat classifies before retrying.
   */
  send(message: OutgoingChatMessage): Promise<ChatMessage | undefined>;
  onMessageAdded(listener: ChatMessageListener): Unsubscribe;
  onMessageUpdated(listener: ChatMessageListener): Unsubscribe;
}

/**
 * Why a conversation could not be opened, in terms the chat can act on. A
 * `forbidden` refusal is what lets Primum Support fall back to reading an
 * inquiry it is not a participant in, so an adapter must report its
 * provider's refusal as `forbidden` and not as a generic failure.
 */
export type ChatTransportErrorKind = 'not_found' | 'forbidden' | 'connection';

export class ChatTransportError extends Error {
  readonly kind: ChatTransportErrorKind;

  readonly cause: unknown;

  constructor(kind: ChatTransportErrorKind, message: string, cause?: unknown) {
    super(message);

    this.name = 'ChatTransportError';

    this.kind = kind;

    this.cause = cause;
  }
}

/** The `clientMessageId` a stored message was sent with, when its sender's app gave one. */
export function clientMessageIdOf(message: ChatMessage): string | undefined {
  const { attributes } = message;

  if (attributes && typeof attributes === 'object' && 'clientMessageId' in attributes) {
    const { clientMessageId } = attributes as { clientMessageId: unknown };

    return typeof clientMessageId === 'string' ? clientMessageId : undefined;
  }

  return undefined;
}

/** The kind of a transport failure. Anything an adapter did not classify is a connection failure. */
export function chatTransportErrorKind(error: unknown): ChatTransportErrorKind {
  return error instanceof ChatTransportError ? error.kind : 'connection';
}

/** Opens conversations for the signed-in viewer. */
export interface ChatTransport {
  /**
   * Rejects when the conversation does not exist or the viewer may not read
   * it, with a {@link ChatTransportError} saying which.
   */
  getConversation(conversationId: string): Promise<ChatConversation>;
  shutdown(): Promise<void>;
}
